// src/pages/TeacherDashboard.js

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChildrenWithReportStatus } from '../actions/reportActions';
import { fetchDailyReport, updateDailyReport } from '../actions/dailyReportActions';
import { fetchMealsByDay } from '../actions/mealActions';
import { fetchGroupReports } from '../actions/groupReportActions';
import ReportsTable from '../components/ReportsTable';
import ViewReports from '../components/ViewReports';
import {
  Typography,
  Container,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Divider,
  Alert
} from '@mui/material';
import PropTypes from 'prop-types';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teacher-tabpanel-${index}`}
      aria-labelledby={`teacher-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const TeacherDashboard = () => {
  const dispatch = useDispatch();
  const [mealOptions, setMealOptions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const date = new Date().toISOString().split('T')[0];
  const jsDay = new Date().getDay();
  const dayOfWeek = jsDay === 0 || jsDay === 6 ? null : jsDay; // Monday=1, Tuesday=2, ..., Friday=5

  // Feedback state for saving daily report info
  const [dailyReportSuccess, setDailyReportSuccess] = useState('');
  const [dailyReportError, setDailyReportError] = useState('');

  // Redux store
  const childrenWithStatus = useSelector((state) => state.reports.childrenWithStatus);
  const dailyReport = useSelector((state) => state.dailyReport.dailyReport);
  const groupReports = useSelector((state) => state.groupReports.groupReports);

  // Local state for daily report inputs
  // "photoLink" is the single daily photo/gallery link exposed in UI.
  const [photoLink, setPhotoLink] = useState('');
  const [generalInfo, setGeneralInfo] = useState('');

  useEffect(() => {
    dispatch(fetchChildrenWithReportStatus());
    dispatch(fetchDailyReport(date));
    dispatch(fetchGroupReports(date));
    if (dayOfWeek) {
      dispatch(fetchMealsByDay(dayOfWeek))
        .then((data) => {
          setMealOptions(data);
        })
        .catch((error) => {
          console.error('Error fetching meals:', error);
          setMealOptions([]);
        });
    } else {
      setMealOptions([]);
    }
  }, [dispatch, date, dayOfWeek]);

  // Update local state when dailyReport changes
  useEffect(() => {
    if (dailyReport) {
      setPhotoLink(dailyReport.photo_link || '');
      setGeneralInfo(dailyReport.general_info || '');
    } else {
      setPhotoLink('');
      setGeneralInfo('');
    }
  }, [dailyReport]);

  const handleDailyReportSave = () => {
    dispatch(
      updateDailyReport({
        date,
        photo_link: photoLink,
        general_info: generalInfo,
        // Keep backend legacy field in sync with the single UI field.
        general_photo_link: photoLink,
      })
    )
      .then(() => {
        setDailyReportSuccess('Informațiile generale au fost salvate cu succes!');
        setDailyReportError('');
        dispatch(fetchDailyReport(date));
      })
      .catch((error) => {
        console.error('Error updating daily report:', error);
        setDailyReportError('Eroare la salvarea informațiilor generale.');
        setDailyReportSuccess('');
      });
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Clear feedback when switching tabs
    setDailyReportSuccess('');
    setDailyReportError('');
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Panou de control al profesorului
      </Typography>

      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Teacher Dashboard Tabs">
        <Tab label="Creare Rapoarte" />
        <Tab label="Vizualizare Rapoarte" />
      </Tabs>

      <TabPanel value={tabIndex} index={0}>
        {/* Create Reports Tab Content */}
        <Typography variant="h6" gutterBottom>
          Informații Generale
        </Typography>
        <TextField
          label="Link Fotografie"
          fullWidth
          margin="normal"
          value={photoLink}
          onChange={(e) => setPhotoLink(e.target.value)}
        />
        <TextField
          label="Informații Generale"
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={generalInfo}
          onChange={(e) => setGeneralInfo(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleDailyReportSave}
          style={{ marginBottom: '20px' }}
        >
          Salvează Informațiile Generale
        </Button>
        {dailyReportSuccess && <Alert severity="success" sx={{ mb: 2 }}>{dailyReportSuccess}</Alert>}
        {dailyReportError && <Alert severity="error" sx={{ mb: 2 }}>{dailyReportError}</Alert>}

        <Divider style={{ margin: '20px 0' }} />

        {childrenWithStatus && childrenWithStatus.length > 0 ? (
          <ReportsTable
            children={childrenWithStatus}
            groupReports={groupReports}
            mealOptions={mealOptions}
            date={date}
          />
        ) : (
          <Typography>Nu există copii de afișat.</Typography>
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <ViewReports />
      </TabPanel>
    </Container>
  );
};

export default TeacherDashboard;
